<template>
  <div>
    <header-component />
    <div class="container-fluid mt-4">
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <h3 class="mb-0">Reports</h3>
            <div class="btn-group btn-group-sm">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                class="btn"
                :class="report === tab.id ? 'btn-primary' : 'btn-outline-primary'"
                @click="changeReport(tab.id)"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-2 align-items-start">
            <input
              v-model="search"
              type="text"
              class="form-control form-control-sm"
              style="width:180px;"
              placeholder="Search customer..."
              @input="debouncedFetch"
            />
            <div v-if="report !== 'outstanding'" class="report-date-range">
              <div class="d-flex" style="gap: 0.5rem;">
                <div class="report-date-field">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :value="displayDate(fromDate)"
                    placeholder="DD-MM-YYYY"
                    readonly
                    tabindex="-1"
                    aria-hidden="true"
                  />
                  <input
                    v-model="fromDate"
                    type="date"
                    class="report-date-picker"
                    aria-label="Start date"
                    @change="reload"
                  />
                  <i class="ni ni-calendar-grid-58 report-date-icon" aria-hidden="true"></i>
                </div>
                <div class="report-date-field">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :value="displayDate(toDate)"
                    placeholder="DD-MM-YYYY"
                    readonly
                    tabindex="-1"
                    aria-hidden="true"
                  />
                  <input
                    v-model="toDate"
                    type="date"
                    class="report-date-picker"
                    aria-label="End date"
                    @change="reload"
                  />
                  <i class="ni ni-calendar-grid-58 report-date-icon" aria-hidden="true"></i>
                </div>
              </div>
              <div class="flex flex-wrap gap-1 mt-1">
                <button type="button" @click="setDateFilter('currentFY')" :class="dateQuickFilterClass('currentFY')">Current FY</button>
                <button type="button" @click="setDateFilter('prevFY')" :class="dateQuickFilterClass('prevFY')">Prev FY</button>
                <button type="button" @click="setDateFilter('today')" :class="dateQuickFilterClass('today')">Today</button>
                <button type="button" @click="setDateFilter('yesterday')" :class="dateQuickFilterClass('yesterday')">Yesterday</button>
                <button type="button" @click="setDateFilter('thisWeek')" :class="dateQuickFilterClass('thisWeek')">This Week</button>
                <button type="button" @click="setDateFilter('thisMonth')" :class="dateQuickFilterClass('thisMonth')">This Month</button>
                <button type="button" @click="setDateFilter('lastMonth')" :class="dateQuickFilterClass('lastMonth')">Last Month</button>
              </div>
            </div>
            <select v-model="branchId" class="form-control form-control-sm" style="width:180px;" @change="reload">
              <option value="">All branches</option>
              <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
            </select>
            <button v-if="hasFilters" type="button" class="btn btn-sm btn-outline-secondary" @click="clearFilters">Clear</button>
            <span v-if="summary" class="ml-auto text-sm text-muted">
              {{ summary.count }} records · Total {{ money(summary.total) }}
            </span>
          </div>
        </div>

        <div v-if="loading" class="p-4 text-center text-muted">Loading...</div>
        <div v-else-if="!items.length" class="p-4 text-center text-muted">No records found.</div>
        <div v-else>
          <table v-if="report === 'invoices'" class="table table-sm table-flush table-striped">
            <thead class="thead-light">
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Branch</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in items" :key="row.invoice_id">
                <td class="text-nowrap">{{ formatDate(row.date) }}</td>
                <td>{{ row.invoice_number }}</td>
                <td>
                  <div>{{ row.customer_name || '—' }}</div>
                  <div class="text-muted" style="font-size:0.85em;">{{ row.phone || '—' }}</div>
                </td>
                <td>
                  <span
                    v-if="row.branch_name"
                    class="inline-flex px-1 py-0.5 text-xs font-semibold rounded-full"
                    :style="branchBadgeStyle(row.branch_color)"
                  >{{ row.branch_name }}</span>
                  <span v-else>—</span>
                </td>
                <td>{{ money(row.amount) }}</td>
                <td>{{ money(row.amount_paid) }}</td>
                <td>{{ money(row.balance) }}</td>
              </tr>
            </tbody>
          </table>

          <table v-else-if="report === 'collections'" class="table table-sm table-flush table-striped">
            <thead class="thead-light">
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Source</th>
                <th>Mode</th>
                <th>Branch</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in items" :key="row.id">
                <td class="text-nowrap">{{ formatDate(row.date) }}</td>
                <td>
                  <div>{{ row.customer_name || '—' }}</div>
                  <div class="text-muted" style="font-size:0.85em;">{{ row.phone || '—' }}</div>
                </td>
                <td>
                  <div>{{ row.source }}</div>
                  <div class="text-muted" style="font-size:0.85em;">{{ row.reference || '—' }}</div>
                </td>
                <td>{{ row.payment_mode || '—' }}</td>
                <td>
                  <span
                    v-if="row.branch_name"
                    class="inline-flex px-1 py-0.5 text-xs font-semibold rounded-full"
                    :style="branchBadgeStyle(row.branch_color)"
                  >{{ row.branch_name }}</span>
                  <span v-else>—</span>
                </td>
                <td>{{ money(row.amount) }}</td>
              </tr>
            </tbody>
          </table>

          <table v-else class="table table-sm table-flush table-striped">
            <thead class="thead-light">
              <tr>
                <th>Customer</th>
                <th>Service dues</th>
                <th>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in items" :key="row.user_id">
                <td>
                  <div>{{ row.customer_name || '—' }}</div>
                  <div class="text-muted" style="font-size:0.85em;">{{ row.phone || '—' }}</div>
                </td>
                <td>{{ money(row.service_dues) }}</td>
                <td class="font-weight-bold text-danger">{{ money(row.outstanding) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-if="meta" class="card-footer">
            <div class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between">
              <p class="card-category">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</p>
              <pagination :meta="meta" @paginate="onPaginate" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import {
  formatDisplayDate,
  dateRangeForPreset,
  matchingDatePreset
} from "@/utils/dateRangeFilters";

const LARAVEL = endpoints.LARAVEL_BASE_URL;

export default {
  components: {
    "header-component": headerComponent,
    pagination,
  },
  data() {
    return {
      tabs: [
        { id: "invoices", label: "Invoice Report" },
        { id: "collections", label: "Collection Report" },
        { id: "outstanding", label: "Outstanding Report" },
      ],
      report: "invoices",
      branches: [],
      branchId: "",
      search: "",
      fromDate: "",
      toDate: "",
      items: [],
      meta: null,
      summary: null,
      page: 1,
      loading: false,
      _fetchTimer: null,
    };
  },
  computed: {
    hasFilters() {
      return this.search || this.fromDate || this.toDate || this.branchId;
    },
    activeDatePreset() {
      return matchingDatePreset(this.fromDate, this.toDate);
    },
  },
  mounted() {
    apiHelper.call("get", LARAVEL + "/api/v1/branches")
      .then(res => { this.branches = res.data.data || res.data || []; })
      .catch(() => {});
    this.fetchReport();
  },
  methods: {
    changeReport(id) {
      this.report = id;
      this.page = 1;
      this.fetchReport();
    },
    reload() {
      this.page = 1;
      this.fetchReport();
    },
    debouncedFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => this.reload(), 350);
    },
    clearFilters() {
      this.search = "";
      this.fromDate = "";
      this.toDate = "";
      this.branchId = "";
      this.reload();
    },
    displayDate(value) {
      return formatDisplayDate(value);
    },
    setDateFilter(type) {
      const range = dateRangeForPreset(type);
      if (!range) return;
      this.fromDate = range.fromDate;
      this.toDate = range.toDate;
      this.reload();
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    onPaginate(data) {
      this.page = data.page;
      this.fetchReport();
    },
    fetchReport() {
      this.loading = true;
      const params = new URLSearchParams({ page: this.page, per_page: 20 });
      if (this.search) params.append("search", this.search);
      if (this.branchId) params.append("branch_id", this.branchId);
      if (this.report !== "outstanding") {
        if (this.fromDate) params.append("from_date", this.fromDate);
        if (this.toDate) params.append("to_date", this.toDate);
      }
      apiHelper.call("get", LARAVEL + "/api/v1/reports/" + this.report + "?" + params.toString())
        .then(res => {
          this.items = res.data.items || [];
          this.meta = res.data.meta || null;
          this.summary = res.data.summary || null;
        })
        .catch(() => {
          this.items = [];
          this.meta = null;
          this.summary = null;
        })
        .finally(() => { this.loading = false; });
    },
    formatDate(value) {
      if (!value) return "—";
      const raw = String(value).trim();
      const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return raw;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${date.getFullYear()}`;
    },
    money(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    branchBadgeStyle(color) {
      if (!color) return { background: "#dbeafe", color: "#1e40af" };
      return { background: color, color: "#fff" };
    },
  },
};
</script>

<style scoped>
.report-date-field {
  position: relative;
  width: 150px;
}

.report-date-field .form-control {
  padding-right: 1.75rem;
  background-color: #fff;
}

.report-date-picker {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.report-date-picker::-webkit-calendar-picker-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
}

.report-date-icon {
  position: absolute;
  top: 50%;
  right: 0.55rem;
  z-index: 1;
  font-size: 0.85rem;
  color: #8898aa;
  pointer-events: none;
  transform: translateY(-50%);
}
</style>
